import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  Users, 
  Target, 
  TrendingUp, 
  Calendar,
  Briefcase,
  CheckCircle,
  ArrowRight,
  Brain,
  Shield,
  Zap,
  Award,
  Timer,
  Sparkles,
  ChevronRight,
  ChevronDown,
  Quote
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  Legend as ReLegend,
} from 'recharts';
import SiteCTA from '@/components/SiteCTA';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';

interface ServiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  features: string[];
  highlight?: string;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ icon, title, description, features, highlight }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      whileHover={{ y: -5, scale: 1.02 }}
      className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl rounded-2xl p-8 border border-gold/20 hover:border-gold/40 transition-all duration-300 relative overflow-hidden group h-full"
    >
      {/* Hover glow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-gold/0 to-gold/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
      
      {highlight && (
        <div className="absolute top-4 right-4">
          <span className="bg-gradient-to-r from-gold to-amber-400 text-black text-xs font-bold px-2 py-1 rounded-full">
            {highlight}
          </span>
        </div>
      )}
      
      <div className="relative z-10">
        <div className="w-12 h-12 bg-gradient-to-br from-gold/20 to-amber-400/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
          {icon}
        </div>
        
        <h3 className="text-xl font-bold text-white mb-3">{title}</h3>
        <p className="text-gray-400 mb-4">{description}</p>
        
        <ul className="space-y-2">
          {features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
              <span className="text-gray-300 text-sm">{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

// Animated background component (standard grid hero)
const AnimatedBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black" />
      {/* Animated grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:24px_24px]" />
      {/* Floating orbs */}
      <motion.div
        className="absolute top-20 right-20 w-96 h-96 bg-gold/20 rounded-full blur-3xl"
        animate={{ x: [0, 100, 0], y: [0, -50, 0] }}
        transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-20 left-20 w-72 h-72 bg-amber-500/20 rounded-full blur-3xl"
        animate={{ x: [0, -50, 0], y: [0, 100, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold/10 rounded-full blur-3xl"
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      />
    </div>
  );
};

const FractionalPMOPage = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, 50]);
  const [hoveredKey, setHoveredKey] = React.useState<string | null>(null);

  // Chart data derived from /chart.js (language + numbers only)
  const chartData = [
    { key: 'tier-20', label: 'Strategic Advisor', sub: '20 hrs./month', director: 220000, fractional: 48000, hrsPerYear: 240 },
    { key: 'tier-40', label: 'Active Leader', sub: '40 hrs./month', director: 220000, fractional: 91200, hrsPerYear: 480 },
    { key: 'tier-60', label: 'Transformation Driver', sub: '60 hrs./month', director: 220000, fractional: 132000, hrsPerYear: 720 },
    { key: 'tier-80', label: 'Executive Partner', sub: '80 hrs./month', director: 220000, fractional: 168000, hrsPerYear: 960 },
  ];

  // Custom centered Y-axis label for the chart
  const YAxisLabel: React.FC<any> = ({ viewBox }) => {
    if (!viewBox) return null;
    const { x = 0, y = 0, height = 0 } = viewBox as any;
    const midY = y + height / 2;
    const xPos = x + 12; // slight padding inside left
    return (
      <text
        x={xPos}
        y={midY}
        fill="#d4af37"
        textAnchor="middle"
        transform={`rotate(-90, ${xPos}, ${midY})`}
        style={{ fontSize: 12, fontWeight: 500 }}
      >
        Estimated Annual Cost
      </text>
    );
  };

  const services = [
    {
      icon: <Brain className="w-6 h-6 text-gold" />,
      title: "Strategic PMO Leadership",
      description: "Executive-level PMO guidance without the full-time commitment or cost.",
      features: [
        "PMO vision and strategy development",
        "Executive stakeholder management",
        "Portfolio governance framework design",
        "Strategic alignment and prioritization"
      ],
      highlight: "C-Suite Ready"
    },
    {
      icon: <Shield className="w-6 h-6 text-gold" />,
      title: "PMO Maturity Acceleration",
      description: "Expert leadership to rapidly advance your PMO's capabilities and impact.",
      features: [
        "Maturity assessment and roadmap",
        "Best practice implementation",
        "Process optimization and standardization",
        "Change management and adoption"
      ]
    },
    {
      icon: <Users className="w-6 h-6 text-gold" />,
      title: "Team Development & Mentoring",
      description: "Build high-performing PMO teams with expert coaching and development.",
      features: [
        "PM/PMO team skill development",
        "Leadership coaching and mentoring",
        "Career pathway design",
        "Knowledge transfer programs"
      ]
    },
    {
      icon: <Target className="w-6 h-6 text-gold" />,
      title: "Interim PMO Leadership",
      description: "Experienced PMO leadership during transitions or executive searches.",
      features: [
        "Seamless leadership continuity",
        "Maintain momentum during transitions",
        "Executive search support",
        "Smooth handoff to permanent leader"
      ],
      highlight: "Gap Coverage"
    },
    {
      icon: <TrendingUp className="w-6 h-6 text-gold" />,
      title: "PMO Performance Optimization",
      description: "Data-driven improvements to maximize your PMO's business value.",
      features: [
        "KPI and metrics framework",
        "Performance dashboards and reporting",
        "Value realization tracking",
        "Continuous improvement programs"
      ]
    },
    {
      icon: <Zap className="w-6 h-6 text-gold" />,
      title: "Agile PMO Transformation",
      description: "Evolve your traditional PMO to support agile and hybrid delivery.",
      features: [
        "Agile governance models",
        "Hybrid methodology integration",
        "Scaled agile implementation",
        "Modern PMO operating models"
      ]
    }
  ];

  const opportunities = [
    'We are facing a leadership gap in our PMO and need experienced guidance to maintain momentum.',
    'Our organization is undergoing change, and we require interim PMO leadership to ensure continuity.',
    'We want to develop our internal PMO talent through mentoring and leadership coaching.',
    'We need an experienced PMO leader to guide us through a critical period of change and uncertainty.',
    'Our current PMO leadership is stretched thin, creating gaps in oversight and strategic focus.',
    'We need strategic PMO leadership to stabilize operations while we rethink our long-term structure.'
  ];

  const engagementModels = [
    {
      title: "Part-Time Executive",
      duration: "2-3 days/week",
      ideal: "Organizations needing strategic leadership",
      icon: <Briefcase className="w-5 h-5" />
    },
    {
      title: "Advisory Retainer",
      duration: "5-10 hours/week",
      ideal: "Established PMOs needing guidance",
      icon: <Brain className="w-5 h-5" />
    },
    {
      title: "Project-Based",
      duration: "Specific initiatives",
      ideal: "Targeted transformation efforts",
      icon: <Target className="w-5 h-5" />
    },
    {
      title: "Interim Full-Time",
      duration: "3-6 months",
      ideal: "Leadership transitions",
      icon: <Timer className="w-5 h-5" />
    }
  ];

  const benefits = [
    {
      title: "Cost-Effective Excellence",
      description: "Access senior PMO expertise at a fraction of full-time executive cost."
    },
    {
      title: "Flexible Engagement",
      description: "Scale leadership involvement up or down based on your needs."
    },
    {
      title: "Immediate Impact",
      description: "Experienced leadership, driving results from day 1 with visible momentum."
    },
    {
      title: "Knowledge Transfer",
      description: "Playbooks delivered, sponsor alignment, successor ready."
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Hero Section - Standard Grid Styled */}
      <section className="relative min-h-screen flex items-center justify-center">
        <AnimatedBackground />

        <motion.div 
          style={{ y }}
          className="container mx-auto px-4 relative z-10 text-center"
        >
          {/* Badge */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-3 bg-gradient-to-r from-gold/20 to-amber-400/20 rounded-full px-6 py-3 mb-8 border border-gold/30 backdrop-blur-sm"
          >
            <Award className="w-5 h-5 text-gold" />
            <span className="text-gold font-bold text-sm uppercase tracking-wider">Executive PMO Leadership On-Demand</span>
            <Award className="w-5 h-5 text-gold" />
          </motion.div>

          {/* Heading */}
          <motion.h1 
            className="text-5xl md:text-7xl lg:text-8xl font-bold leading-tight mb-6"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="block text-white">
              Expert PMO Leadership.
            </span>
            <motion.span 
              className="block text-transparent bg-clip-text bg-gradient-to-r from-gold via-amber-300 to-gold animate-gradient mt-4"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              On Your Terms.
            </motion.span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p 
            className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Senior Expertise on Demand. Full Time Results. No Overhead. Successor Ready Handoff.
          </motion.p>

          {/* Hero Stats (matching Quick Wins style) */}
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto mt-10"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            {[
              { number: '< 30 Days', label: 'Time To Impact' },
              { number: '3.5x', label: 'Avg. ROI' },
              { number: '50%', label: 'Cost Savings' },
              { number: '4 Weeks', label: 'Portfolio Baseline' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.9 + index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-gold mb-2">{stat.number}</div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 inset-x-0 flex justify-center"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-col items-center gap-2 text-gold">
            <span className="text-sm uppercase tracking-wider">Scroll to explore</span>
            <ChevronDown className="w-6 h-6" />
          </div>
        </motion.div>
      </section>

      

      

      

      {/* Benefits Section (moved up) */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-400 mb-4">
              The Fractional Advantage
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Why leading organizations choose OptiGold PMO's Fractional Leadership
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex gap-4"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-gold/20 to-amber-400/20 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-gold" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                  <p className="text-gray-400">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing & Packages */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold via-amber-300 to-gold mb-4">
              Flexible Engagements Tailored To you
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Choose the monthly capacity that fits. Start with 2 months.
            </p>
            <div className="mt-10 flex justify-center">
              <a
                href="https://cal.com/noah.mccloud/optigold-pmo-quick-wins-targeted-solutions"
                className="group inline-flex h-11 items-center justify-center gap-3 whitespace-nowrap rounded-full bg-gradient-to-r from-gold to-amber-400 px-10 text-lg font-bold text-black shadow-2xl transition-all hover:from-amber-400 hover:to-gold hover:shadow-gold/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/50 focus-visible:ring-offset-2"
              >
                Schedule a Discovery Call
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </motion.div>

          {/* Horizontal pricing cards */}
          <div className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl rounded-3xl border border-gold/20 p-6 md:p-8 shadow-2xl">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {[
                {
                  key: 'strategic-20',
                  name: 'Strategic Advisor',
                  hours: '20',
                  price: '$4,000 / mo',
                  tagline: 'Executive advisory.',
                  bullets: [
                    'Advisory and health check',
                    'Portfolio visibility baseline',
                    'Weekly status and risk review',
                    'Executive one‑pager weekly'
                  ],
                  badge: ''
                },
                {
                  key: 'active-40',
                  name: 'Active Leader',
                  hours: '40',
                  price: '$7,600 / mo',
                  tagline: 'Cadence and coaching.',
                  bullets: ['All from 20 hours', 'Intake and gating cadence', 'PM coaching and issue triage', 'Executive check-in (bi-weekly or as needed)'],
                  badge: ''
                },
                {
                  key: 'transform-60',
                  name: 'Transformation Driver',
                  hours: '60',
                  price: '$11,000 / mo',
                  tagline: 'Governance and reporting.',
                  bullets: ['All from 40 hours', 'Portfolio Governance Optimization', 'Standards, process and reporting uplift', 'Change and communications support'],
                  badge: 'Most Popular'
                },
                {
                  key: 'exec-80',
                  name: 'Executive Partner',
                  hours: '80',
                  price: '$14,000 / mo',
                  tagline: 'Interim PMO leadership.',
                  bullets: ['Includes all from 60 hours', 'Day‑to‑day portfolio management', 'Executive reporting and stakeholder management', 'Continuous improvement initiatives'],
                  badge: ''
                }
              ].map((plan) => (
                <div
                  key={plan.key}
                  className={`relative rounded-2xl bg-gradient-to-br from-gray-900/70 to-black/70 p-6 transition-all group ${plan.badge ? 'border-2 border-gold' : 'border border-gold/20 hover:border-gold/40'}`}
                >
                  {plan.badge && (
                    <span className="absolute -top-3 right-4 px-3 py-1 rounded-full bg-gradient-to-r from-gold to-amber-400 text-black text-xs font-bold shadow-lg">
                      {plan.badge}
                    </span>
                  )}
                  <div className="flex items-center justify-between mb-3">
                    <span className="inline-block px-3 py-1 rounded-full bg-gradient-to-r from-gold/20 to-amber-400/20 border border-gold/30 text-gold text-sm font-bold">
                      {plan.name}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-3xl font-bold text-white">{plan.hours}</span>
                    <span className="text-sm text-gray-400">hrs./month</span>
                  </div>
                  {plan.tagline && (
                    <div className="text-gray-300 text-sm mb-4">{plan.tagline}</div>
                  )}
                  <div className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-400 text-2xl font-extrabold mb-4">
                    {plan.price}
                  </div>
                  {plan.bullets.length > 0 && (
                    <ul className="space-y-2">
                      {plan.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-300 text-sm">
                          <CheckCircle className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                          <span>{b.startsWith('✓') ? b.slice(1) : b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
            <div className="text-center mt-6 text-gray-400">
              Need something in between? <Link to="/contact" className="text-gold underline-offset-4 hover:underline">Contact Us</Link> for a custom quote.
            </div>
          </div>

          
          
        </div>
      </section>

      {/* Cost Comparison Chart (moved directly below Pricing) */}
      <section className="py-16 relative">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-gray-900/70 to-black/70 backdrop-blur-xl rounded-3xl border border-gold/20 p-6 md:p-8 shadow-2xl"
          >
            <div className="flex items-end justify-between mb-6">
              <h3 className="text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold via-amber-300 to-gold leading-snug">
                <span className="block">The OptiGold Advantage</span>
                <span className="block">Full-Time Executive Total Comp vs. OptiGold Fractional Leader</span>
              </h3>
            </div>
            <div style={{ width: '100%', height: 360 }}>
              <ResponsiveContainer>
                <BarChart data={chartData} barGap={8}>
                  <defs>
                    <linearGradient id="goldGradient" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#d4af37" />
                      <stop offset="100%" stopColor="#f59e0b" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(212,175,55,0.15)" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: '#9CA3AF', fontSize: 12 }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgba(212,175,55,0.25)' }}
                    interval={0}
                  />
                  <YAxis
                    tickFormatter={(v) => `$${(v / 1000)}k`}
                    tick={{ fill: '#9CA3AF', fontSize: 12 }}
                    axisLine={{ stroke: 'rgba(212,175,55,0.25)' }}
                    tickLine={false}
                    label={<YAxisLabel />}
                  />
                  <ReTooltip
                    cursor={false}
                    wrapperStyle={{ outline: 'none' }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload || payload.length === 0) return null;
                      const row = chartData.find((d) => d.label === label);
                      const items = hoveredKey ? payload.filter((p: any) => p.dataKey === hoveredKey) : [payload[0]];
                      const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(n);
                      return (
                        <div className="bg-black/80 border border-gold/30 rounded-xl p-3 text-sm">
                          <div className="text-white font-semibold mb-1">{label} <span className="text-gray-400">• {row?.sub}</span></div>
                          {items.map((p: any, i: number) => (
                            <div key={i} className="text-gray-200">
                              {p.dataKey === 'director' ? (
                                <>
                                  Comparable Full-Time Executive (Total Comp): <span className="text-gold">{fmt(p.value)}</span> — PMO Director
                                </>
                              ) : (
                                <>
                                  OptiGold Fractional PMO (Retainer & Immediate Access): <span className="text-gold">{fmt(p.value)}</span> • {row?.hrsPerYear} hrs/year
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    }}
                  />
                  <ReLegend
                    verticalAlign="bottom"
                    align="center"
                    iconType="circle"
                    wrapperStyle={{ color: '#D4AF37', marginTop: 8 }}
                    payload={[
                      { value: 'Comparable Full-Time Executive (Total Comp)', type: 'circle', id: 'legend1', color: '#9CA3AF' },
                      { value: 'OptiGold Fractional PMO (Retainer & Immediate Access)', type: 'circle', id: 'legend2', color: '#d4af37' },
                    ]}
                  />
                  <Bar
                    dataKey="director"
                    name="Comparable Full-Time Executive (Total Comp)"
                    fill="#9CA3AF"
                    radius={[6,6,0,0]}
                    onMouseEnter={() => setHoveredKey('director')}
                    onMouseLeave={() => setHoveredKey(null)}
                  />
                  <Bar
                    dataKey="fractional"
                    name="OptiGold Fractional PMO (Retainer & Immediate Access)"
                    fill="url(#goldGradient)"
                    radius={[6,6,0,0]}
                    onMouseEnter={() => setHoveredKey('fractional')}
                    onMouseLeave={() => setHoveredKey(null)}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Opportunities to Unlock Value (match Quick Wins styling) */}
      <section className="py-20 relative bg-gradient-to-b from-black via-gray-900 to-black overflow-hidden">
        {/* Ambient orbs */}
        <motion.div
          className="absolute -top-10 -left-10 w-80 h-80 bg-gold/10 rounded-full blur-3xl"
          animate={{ x: [0, 30, 0], y: [0, 20, 0], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute -bottom-10 -right-10 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"
          animate={{ x: [0, -30, 0], y: [0, -20, 0], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-300">
                Opportunities to Unlock Value
              </span>
            </h2>
          </motion.div>

          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {opportunities.map((text, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
                whileHover={{ y: -4, scale: 1.02 }}
                className="relative group"
              >
                <div className="absolute -inset-0.5 bg-gradient-to-br from-gold/0 via-gold/20 to-amber-400/0 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl border border-gold/20 rounded-2xl p-6 h-full">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold to-amber-400 flex items-center justify-center flex-shrink-0">
                      <Quote className="w-5 h-5 text-black" />
                    </div>
                    <p className="text-gray-200 leading-relaxed">{text}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Questions */}
      <section className="py-16 relative">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h3 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-400">
              Common Questions
            </h3>
          </motion.div>
          <div className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl rounded-3xl border border-gold/20 p-4 md:p-6 shadow-2xl max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1" className="border-gold/20">
                <AccordionTrigger className="text-white">What are the terms?</AccordionTrigger>
                <AccordionContent className="text-gray-300">
                  Monthly retainer, 2 month minimum with 30-day notice to end or scale down. Travel and special projects are separate.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="border-gold/20">
                <AccordionTrigger className="text-white">What is in scope for fractional PMO leadership?</AccordionTrigger>
                <AccordionContent className="text-gray-300">
                  Each pricing tier highlights scope. Tiers build on one another for greater scope with each step up.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="border-gold/20">
                <AccordionTrigger className="text-white">Do you make small fixes or only advise?</AccordionTrigger>
                <AccordionContent className="text-gray-300">
                  The Executive Partner Tier includes continuous improvement and regular check ins. Minor fixes included. Major changes scoped separately.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4" className="border-gold/20">
                <AccordionTrigger className="text-white">Can unused hours roll?</AccordionTrigger>
                <AccordionContent className="text-gray-300">
                  Yes. Unused hours roll one month. We keep a shared log for full transparency.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-5" className="border-gold/20">
                <AccordionTrigger className="text-white">Will potential inefficiencies or unnecessary expenses be addressed?</AccordionTrigger>
                <AccordionContent className="text-gray-300">
                  Yes. We surface inefficiencies and provide clear remedies the client can action, or we can scope a separate engagement.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Services Grid removed per request */}

      {/* Benefits Section moved above */}

      {/* Success Metrics removed: stats moved to hero */}

      {/* Standard Site CTA */}
      <SiteCTA />
    </div>
  );
};

export default FractionalPMOPage;
